import { Component, EventEmitter, Output } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
    faDownload,
    faPlusCircle,
    faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { AsyncModalService } from "src/app/services/shared/async-modal-service";
import { EmployeesService } from "../../../services/settings/employees.service";

@Component({
    standalone: false,
    selector: "employee-form",
    templateUrl: "./employees.form.component.html",
    providers: [EmployeesService],
})

export class FormEmployees {
    private employeeId: number;
    @Output("cancelEdit")
    cancelEdit: EventEmitter<number> = new EventEmitter<number>();
    @Output("refreshTable")
    refreshTable: EventEmitter<void> = new EventEmitter<void>();
    private roles: any[] = [];
    showButtons: boolean;
    isAdd: boolean;
    showImport: boolean;
    isEdit: boolean;
    downloadWithData: boolean = false;
    private form: FormGroup;
    private employee: any = {};
    private fields: Array<FormlyFieldConfig> = [];
    uploadableFile: File;
    faDownload = faDownload;
    faUpload = faUpload;
    faPlusSign = faPlusCircle;
    private asyncModalService: AsyncModalService;

    constructor(
        private fb: FormBuilder,
        private service: EmployeesService,
        private route: ActivatedRoute,
        private router: Router,
        asyncModalService: AsyncModalService
    ) {
        this.asyncModalService = asyncModalService;

        this.resetView();
    }

    getFieldGroups() {
        return [
            {
                fieldGroup: [
                    {
                        key: "employeeNumber",
                        type: "hz-input",
                        props: {
                            type: "text",
                            label: "Employee Number",
                            placeholder: "No.",
                            required: true,
                            pattern: "(^[A-Za-z0-9]*$)",
                        },
                        validation: {
                            messages: {
                                pattern: "Special characters are not allowed",
                            },
                        },
                    },
                    {
                        key: "firstName",
                        type: "hz-input",
                        props: {
                            type: "text",
                            label: "First Name",
                            placeholder: "Name",
                            required: true,
                        },
                        validators: {
                            firstName: {
                                expression: (c: AbstractControl) => !c.value || /^\S(.*\S)?$/.test(c.value),
                                message: "Leading or trailing whitespace is not allowed",
                            },
                            firstNameChars: {
                                expression: (c: AbstractControl) => !c.value || /^[\p{L}\s'-]+$/u.test(c.value),
                                message: "Special characters are not allowed",
                            },
                        },
                    },
                    {
                        key: "lastName",
                        type: "hz-input",
                        props: {
                            type: "text",
                            label: "Last Name",
                            placeholder: "Last Name",
                            required: true,
                        },
                        validators: {
                            lastName: {
                                expression: (c: AbstractControl) => !c.value || /^\S(.*\S)?$/.test(c.value),
                                message: "Leading or trailing whitespace is not allowed",
                            },
                            lastNameChars: {
                                expression: (c: AbstractControl) => !c.value || /^[\p{L}\s'-]+$/u.test(c.value),
                                message: "Special characters are not allowed",
                            },
                        },
                    },
                    {
                        key: "email",
                        type: "hz-input",
                        props: {
                            type: "email",
                            label: "Email",
                            placeholder: "Email Address",
                        },
                        validators: {
                            email: {
                                expression: (c: AbstractControl) => !c.value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.value),
                                message: "Please enter a valid email address",
                            },
                        },
                    },
                    {
                        key: "mobile",
                        type: "hz-input",
                        props: {
                            type: "tel",
                            label: "Mobile Number",
                            placeholder: "Mobile Number",
                        },
                        validators: {
                            mobile: {
                                expression: (c: AbstractControl) => !c.value || /^\S(.*\S)?$/.test(c.value),
                                message: "Leading or trailing whitespace is not allowed",
                            },
                            mobileChars: {
                                expression: (c: AbstractControl) => !c.value || /^[0-9+\-() ]+$/.test(c.value),
                                message: "Only numbers and + - ( ) are allowed",
                            },
                        },
                    },
                    {
                        key: "landline",
                        type: "hz-input",
                        props: {
                            type: "tel",
                            label: "Landline Number",
                            placeholder: "Landline Number",
                        },
                        validators: {
                            landline: {
                                expression: (c: AbstractControl) => !c.value || /^\S(.*\S)?$/.test(c.value),
                                message: "Leading or trailing whitespace is not allowed",
                            },
                            landlineChars: {
                                expression: (c: AbstractControl) => !c.value || /^[0-9+\-() ]+$/.test(c.value),
                                message: "Only numbers and + - ( ) are allowed",
                            },
                        },
                    },
                    {
                        key: "startDate",
                        type: "date",
                        defaultValue: this.getToday(),
                        props: {
                            type: "date",
                            label: "Start Date",
                            placeholder: "dd-MM-yyyy",
                        },
                    },
                    {
                        key: "role",
                        type: "hz-select",
                        props: {
                            label: "Role",
                            required: true,
                            options: this.roles,
                        },
                    },
                    {
                        key: "type",
                        type: "hz-select",
                        props: {
                            required: false,
                            label: "Type",
                            options: [
                                {
                                    value: "Administration",
                                    label: "Administration",
                                },
                                { value: "Management", label: "Management" },
                                { value: "Labour", label: "Labour" },
                            ],
                        },
                    },
                ],
            },
        ];
    }

    ngOnDestroy() {}

    refreshRoles() {
        if (!this.roles || this.roles.length == 0) {
            this.service.getAllRoles().subscribe((data) => {
                this.roles = data;

                var fieldGroup = this.getFieldGroup("role");

                if (fieldGroup) fieldGroup.props.options = this.roles;
            });
        }
    }

    process(employee: any) {
        if (this.form.valid) {
            if (this.isAdd) {
                this.submitAdd(employee);
            } else {
                this.submitUpdate(employee);
            }
        } else {
            this.asyncModalService.getModalResponse(
                "Please fill in mandatory fields"
            );
        }
    }

    submitUpdate(employee: FormEmployees) {
        this.service.updateEmployee(this.employeeId, employee).subscribe(
            null,
            (err) => this.asyncModalService.getModalResponse(err.error),
            () => {
                this.router.navigate(["../../"], { relativeTo: this.route });
            }
        );
    }

    submitAdd(employee: FormEmployees) {
        this.service.addEmployee(employee).subscribe(
            null,
            (err) => this.asyncModalService.getModalResponse(err.error),
            () => {
                this.resetView();
                this.cancelEdit.emit(this.employeeId);

                this.refreshTable.emit();
            }
        );
    }

    fileChangeEvent(fileInput: any) {
        this.uploadableFile = fileInput.target.files[0] as File;
    }

    uploadFile(data) {
        const formData: any = new FormData();
        const file: File = this.uploadableFile;

        formData.append("uploads[]", file, file["name"]);

        this.service.uploadFile(formData).subscribe(
            null,
            (err) => {
                this.resetView();
                this.asyncModalService.getModalResponse(err.error);
                this.refreshTable.emit();
            },
            () => {
                this.resetView();

                this.refreshTable.emit();
            }
        );
    }

    resetView() {
        this.form = this.fb.group({});
        this.employee = {};
        this.employeeId = 0;
        this.showImport = false;
        this.isAdd = false;
        this.showButtons = false;
        this.isEdit = false;
        this.fields = this.getFieldGroups();
        this.refreshRoles();

        var id = +this.route.snapshot.params["id"];
        if (id && id > 0) {
            this.employeeId = id;

            this.bindEmployee(this.employeeId);
        } else {
            this.showButtons = true;
        }
    }

    bindEmployee(employeeId: number) {
        this.service.getEmployee(employeeId).subscribe(
            (data) => {
                this.employee = data;
                this.form = this.fb.group({});
                this.fields = this.getFieldGroups();
            },
            (err) => this.asyncModalService.getModalResponse(err.error),
            () => {
                this.isEdit = true;
            }
        );
    }

    showAdd() {
        this.resetView();
        this.isAdd = true;
        this.showButtons = false;
    }

    showImportView() {
        this.showImport = true;
        this.isAdd = false;
        this.isEdit = false;
        this.showButtons = false;
    }

    cancel() {
        if (this.isEdit) {
            this.router.navigate(["../../"], { relativeTo: this.route });
        } else {
            this.resetView();
        }
    }

    saveAndNew() {
        if (this.form.valid) {
            this.service.addEmployee(this.employee).subscribe(
                null,
                (err) => this.asyncModalService.getModalResponse(err.error),
                () => {
                    this.resetView();
                    this.isAdd = true;
                    this.showButtons = false;
                    this.refreshTable.emit();
                }
            );
        } else {
            this.asyncModalService.getModalResponse(
                "Please fill in mandatory fields"
            );
        }
    }

    getFieldGroup(key: string): any {
        for (let i = 0; i < this.fields[0].fieldGroup.length; i++) {
            const fieldGroup = this.fields[0].fieldGroup[i];

            if (fieldGroup.key == key) {
                return fieldGroup;
            }
        }
        return null;
    }

    private getToday(): string {
        return this.convertDate(new Date());
    }

    private convertDate(date: Date): string {
        let month = `${date.getMonth() + 1}`;
        let day = `${date.getDate()}`;

        if (parseInt(month) < 10) {
            month = `0${month}`;
        }

        if (parseInt(day) < 10) {
            day = `0${day}`;
        }

        return date.getFullYear() + "-" + month + "-" + day;
    }
}
